import { useEffect, useState, useMemo, useCallback } from 'react'
import useIsMounted from '~hooks/useIsMounted'
import { MAINNET_CHAIN_ID, networks } from '~utils/ethereum'

export default function useChainId(adapter) {
    const [chainId, setChainId] = useState(MAINNET_CHAIN_ID)
    const [switchPending, setSwitchPending] = useState(false)

    const isMounted = useIsMounted()

    useEffect(() => {
        if (!adapter) {
            return undefined
        }

        try {
            const newChainId = adapter.getChainId()

            if (isMounted()) {
                setChainId(newChainId)
            }
        } catch (e) {
            console.error('useChainId', e)
        }

        const onChainChanged = (newChainId) => {
            if (isMounted()) {
                setChainId(newChainId)
            }
        }

        adapter.on('chainChanged', onChainChanged)

        return () => {
            adapter.off('chainChanged', onChainChanged)
        }
    }, [adapter, isMounted])

    const switchChain = useCallback(
        async (nextChainId) => {
            if (!adapter) {
                return
            }

            const provider = adapter.getProvider()

            setSwitchPending(true)

            try {
                if (!networks[nextChainId]) {
                    throw new Error(`Chain id "${nextChainId}" is not supported!`)
                }

                // try switching networks
                await provider.request({
                    method: 'wallet_switchEthereumChain',
                    params: [
                        {
                            chainId: nextChainId,
                        },
                    ],
                })
            } catch (switchError) {
                const { getConfig } = networks[nextChainId] || {}

                // This error code indicates that the chain has not been added to MetaMask.
                if (switchError.code === 4902 && typeof getConfig === 'function') {
                    try {
                        // add the new network
                        await provider.request({
                            method: 'wallet_addEthereumChain',
                            params: [
                                {
                                    chainId: nextChainId,
                                    ...getConfig(),
                                },
                            ],
                        })
                    } catch (addError) {
                        console.error(addError)
                    }
                } else {
                    console.error(switchError)
                }
            } finally {
                if (isMounted()) {
                    setSwitchPending(false)
                }
            }
        },
        [adapter, isMounted],
    )

    return useMemo(
        () => ({
            chainId,
            switchChain,
            switchPending,
        }),
        [chainId, switchChain, switchPending],
    )
}
